import type { useSearchParams } from 'react-router'
import type { FilterGroup } from './ProductsContext'
import { useEffect } from 'react'
import { getInvalidFacetSelections } from './facetFilters'

interface UseFacetUrlSyncOptions {
  searchParams: URLSearchParams
  setSearchParams: ReturnType<typeof useSearchParams>[1]
  filterGroups: FilterGroup[]
  isHomologationCountryFilterAvailable: boolean
  isFacetResponseReady: boolean
}

export function useFacetUrlSync({
  searchParams,
  setSearchParams,
  filterGroups,
  isHomologationCountryFilterAvailable,
  isFacetResponseReady,
}: UseFacetUrlSyncOptions) {
  const invalidFacetSelections = getInvalidFacetSelections(searchParams, filterGroups)

  useEffect(() => {
    if (isHomologationCountryFilterAvailable || !searchParams.has('homologation_countries'))
      return

    setSearchParams((previous) => {
      if (!previous.has('homologation_countries'))
        return previous

      const next = new URLSearchParams(previous)
      next.delete('homologation_countries')
      return next
    })
  }, [isHomologationCountryFilterAvailable, searchParams, setSearchParams])

  useEffect(() => {
    if (!isFacetResponseReady || invalidFacetSelections.length === 0)
      return

    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      let hasChanges = false

      for (const { id, availableValues } of invalidFacetSelections) {
        const currentValues = next.getAll(id)
        const validValues = currentValues.filter(value => availableValues.has(value))

        if (validValues.length === currentValues.length)
          continue

        hasChanges = true
        next.delete(id)
        validValues.forEach(value => next.append(id, value))
      }

      if (!hasChanges)
        return previous

      return next
    })
  }, [invalidFacetSelections, isFacetResponseReady, setSearchParams])

  return invalidFacetSelections
}
